import React from 'react';
import { AlertTriangle, Clock, TrendingUp, Lightbulb } from 'lucide-react';

const BottleneckIdentificationPanel = ({ bottlenecks }) => {
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'text-error bg-error/10 border-error/20';
      case 'medium':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'low':
        return 'text-muted-foreground bg-muted/50 border-border';
      default:
        return 'text-muted-foreground bg-muted/50 border-border';
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-warning/10 rounded-lg">
            <AlertTriangle className="w-6 h-6 text-warning" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Bottleneck Identification</h2>
            <p className="text-sm text-muted-foreground">Workflow friction points and optimization opportunities</p>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-error/5 border border-error/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-error" />
              <span className="text-xs text-muted-foreground">High Priority</span>
            </div>
            <p className="text-2xl font-bold text-error">
              {bottlenecks?.filter(b => b?.severity === 'high')?.length}
            </p>
          </div>
          <div className="p-4 bg-warning/5 border border-warning/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-warning" />
              <span className="text-xs text-muted-foreground">Avg Delay</span>
            </div>
            <p className="text-2xl font-bold text-warning">
              {(bottlenecks?.reduce((sum, b) => sum + b?.avgDelay, 0) / bottlenecks?.length)?.toFixed(1)}d
            </p>
          </div>
          <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-muted-foreground">Impacted Cases</span>
            </div>
            <p className="text-2xl font-bold text-blue-500">
              {bottlenecks?.reduce((sum, b) => sum + b?.impactedCases, 0)}
            </p>
          </div>
        </div>

        {/* Bottleneck List */}
        <div className="space-y-4">
          {bottlenecks?.map((bottleneck) => (
            <div key={bottleneck?.id} className="border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-foreground">{bottleneck?.stage}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(bottleneck?.severity)}`}>
                      {bottleneck?.severity?.toUpperCase()}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                      {bottleneck?.type}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>Avg Delay: {bottleneck?.avgDelay} days</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      <span>Impacted: {bottleneck?.impactedCases} cases</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-blue-500 mb-1">Recommendation</p>
                    <p className="text-sm text-foreground">{bottleneck?.recommendation}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BottleneckIdentificationPanel;